import { formatDisplayDateTime } from '@/utils/date';
import {
  PCMP_CHANNEL_LABELS,
  PCMP_TIMELINE_STATUS_COLOR,
} from '../../constants/pcmpProposal.constants';
import type { PcmpProposalStudentClass, PcmpTimelineEntry } from '../../types/pcmpProposal.types';
import { PcmpChangeList } from './PcmpChangeList';
import { PcmpStatusPill } from './PcmpStatusPill';

interface PcmpTimelineEntryItemProps {
  entry: PcmpTimelineEntry;
  students: PcmpProposalStudentClass[];
}

// Status moves carry channel codes for payment source changes.
const statusLabel = (code: string): string =>
  code in PCMP_CHANNEL_LABELS
    ? PCMP_CHANNEL_LABELS[code as keyof typeof PCMP_CHANNEL_LABELS]
    : code;

/** One event in a card's log: verb, time, pill, reason/comment and changes. */
export function PcmpTimelineEntryItem({ entry, students }: PcmpTimelineEntryItemProps) {
  const hasStatusMove = entry.fromStatus !== null && entry.toStatus !== null;
  return (
    <li className="flex flex-col gap-1.5 border-l-2 border-border pl-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{entry.verb}</span>
        {entry.isLatest ? <PcmpStatusPill tone="info">Latest</PcmpStatusPill> : null}
        {entry.pill ? (
          <PcmpStatusPill tone={PCMP_TIMELINE_STATUS_COLOR[entry.status]}>
            {entry.pill}
          </PcmpStatusPill>
        ) : null}
        {hasStatusMove ? (
          <PcmpStatusPill tone="neutral">
            {statusLabel(entry.fromStatus ?? '')} → {statusLabel(entry.toStatus ?? '')}
          </PcmpStatusPill>
        ) : null}
        <span className="text-xs text-muted-foreground">
          {formatDisplayDateTime(entry.occurredAt)}
        </span>
      </div>
      {entry.comment ? (
        <p className="text-sm whitespace-pre-line text-muted-foreground">“{entry.comment}”</p>
      ) : null}
      <PcmpChangeList changes={entry.changes} students={students} />
      {entry.userName ? (
        <span className="text-xs text-muted-foreground">
          — {entry.userName}
          {entry.designationName ? `, ${entry.designationName}` : ''}
        </span>
      ) : null}
    </li>
  );
}
